import React, { useEffect, useState } from 'react';
import {
  Drawer,
  Button,
  IconButton,
  Box,
  Typography,
  drawerClasses,
  FormControlLabel,
  Switch,
  CircularProgress,
  Divider,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Radio,
  Checkbox,
  Chip,
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { InputText } from '../../elements/InputText';
import SelectControl from '../../elements/SelectControl';
import { ErrorBox } from '../../elements/ErrorBox';
import {
  adminPassword,
  adminUpdateUser,
  forcePasswordReset,
  getServiceAreas,
  getUserById,
} from '../../services/userService';
import {
  ClientSite,
  getClientSites,
  mergeClientSites,
} from '../../services/jobService';
import { phoneRegex } from '../../utils';
import { passwordRegisterValidation } from '../Auth/validation';

type Props = {
  open: boolean;
  userId: string | null;
  onClose: () => void;
  onSaved: () => void;
};

type FormValues = {
  email: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  serviceAreaId: string;
  verified: boolean;
  disabled: boolean;
  temporaryPassword: string;
};

const schema = yup.object({
  email: yup.string().email('Enter a valid email').required('Email is required'),
  firstName: yup.string().required('First name is required'),
  lastName: yup.string().required('Last name is required'),
  phoneNumber: yup
    .string()
    .matches(phoneRegex, 'Enter a valid US phone number')
    .required('Phone number is required'),
  serviceAreaId: yup.string().required('Service area is required'),
  verified: yup.boolean().required(),
  disabled: yup.boolean().required(),
  temporaryPassword: yup.string().defined(),
});

export const ClientDetailDrawer = ({ open, userId, onClose, onSaved }: Props) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [confirmResetOpen, setConfirmResetOpen] = useState(false);
  const [responseError, setResponseError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [selectOptions, setSelectOptions] = useState<{ value: string; label: string }[]>([]);
  const [sites, setSites] = useState<ClientSite[]>([]);
  const [sitesLoading, setSitesLoading] = useState(false);
  const [keepSiteId, setKeepSiteId] = useState<string | null>(null);
  const [mergeSiteIds, setMergeSiteIds] = useState<string[]>([]);
  const [mergeLoading, setMergeLoading] = useState(false);
  const [confirmMergeOpen, setConfirmMergeOpen] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    control,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: yupResolver(schema),
    defaultValues: {
      email: '',
      firstName: '',
      lastName: '',
      phoneNumber: '',
      serviceAreaId: '',
      verified: false,
      disabled: false,
      temporaryPassword: '',
    },
  });

  const verified = watch('verified');
  const disabled = watch('disabled');

  const loadSites = async (id: string) => {
    setSitesLoading(true);
    try {
      const res = await getClientSites(id);
      setSites(res?.sites || []);
      setKeepSiteId(null);
      setMergeSiteIds([]);
    } catch (error: unknown) {
      setResponseError((error as Error).message);
    } finally {
      setSitesLoading(false);
    }
  };

  useEffect(() => {
    const loadAreas = async () => {
      try {
        const res = await getServiceAreas();
        setSelectOptions(
          (res || []).map((item: { id: string; name: string }) => ({
            value: item.id,
            label: item.name,
          }))
        );
      } catch (error: unknown) {
        setResponseError((error as Error).message);
      }
    };
    loadAreas();
  }, []);

  useEffect(() => {
    const loadUser = async () => {
      if (!open || !userId) return;
      setLoading(true);
      setResponseError('');
      setSuccessMessage('');
      try {
        const user = await getUserById(userId);
        reset({
          email: user.email || '',
          firstName: user.firstName || '',
          lastName: user.lastName || '',
          phoneNumber: user.client?.phoneNumber || '',
          serviceAreaId: user.client?.serviceArea?.id || '',
          verified: !!user.verified,
          disabled: !!user.disabled,
          temporaryPassword: '',
        });
        await loadSites(userId);
      } catch (error: unknown) {
        setResponseError((error as Error).message);
      } finally {
        setLoading(false);
      }
    };
    loadUser();
  }, [open, userId, reset]);

  const onSubmit = async (values: FormValues) => {
    if (!userId) return;
    setSaving(true);
    setResponseError('');
    setSuccessMessage('');
    try {
      await adminUpdateUser({
        id: userId,
        email: values.email,
        firstName: values.firstName,
        lastName: values.lastName,
        phoneNumber: values.phoneNumber,
        serviceAreaId: values.serviceAreaId,
        verified: values.verified,
        disabled: values.disabled,
      });
      setSuccessMessage('Client account updated.');
      onSaved();
    } catch (error: unknown) {
      setResponseError((error as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const handleSendReset = async () => {
    if (!userId) return;
    setPasswordLoading(true);
    setResponseError('');
    setSuccessMessage('');
    try {
      await forcePasswordReset(userId);
      setSuccessMessage('Password reset email sent. Existing sessions were signed out.');
      setConfirmResetOpen(false);
    } catch (error: unknown) {
      setResponseError((error as Error).message);
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleSetPassword = async () => {
    if (!userId) return;
    const password = watch('temporaryPassword');
    try {
      await passwordRegisterValidation.validate(password);
    } catch (error: unknown) {
      setResponseError((error as Error).message || 'Password does not meet requirements.');
      return;
    }
    setPasswordLoading(true);
    setResponseError('');
    setSuccessMessage('');
    try {
      await adminPassword({ id: userId, password });
      setValue('temporaryPassword', '');
      setSuccessMessage('Temporary password set. Share it with the client securely.');
    } catch (error: unknown) {
      setResponseError((error as Error).message);
    } finally {
      setPasswordLoading(false);
    }
  };

  const toggleMergeSite = (siteId: string) => {
    setMergeSiteIds((prev) =>
      prev.includes(siteId) ? prev.filter((id) => id !== siteId) : [...prev, siteId]
    );
  };

  const handleMerge = async () => {
    if (!userId || !keepSiteId || mergeSiteIds.length === 0) return;
    setMergeLoading(true);
    setResponseError('');
    setSuccessMessage('');
    try {
      const result = await mergeClientSites({
        userId,
        keepSiteId,
        mergeSiteIds,
      });
      setConfirmMergeOpen(false);
      setSuccessMessage(
        `Merged ${result?.mergedSiteIds?.length || mergeSiteIds.length} jobsite(s). ` +
          `${result?.jobsMoved || 0} job(s) moved` +
          (result?.notesUpdated ? '; notes combined in When I Work.' : '.')
      );
      await loadSites(userId);
    } catch (error: unknown) {
      setResponseError((error as Error).message);
      setConfirmMergeOpen(false);
    } finally {
      setMergeLoading(false);
    }
  };

  const keepSite = sites.find((site) => site.id === keepSiteId);
  const mergeSites = sites.filter((site) => mergeSiteIds.includes(site.id));

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        [`& .${drawerClasses.paper}`]: {
          boxShadow: 'none',
          borderLeft: '1px solid #DDDFDD',
          maxWidth: '560px',
          width: '100%',
        },
      }}
    >
      <Box sx={{ py: 3, px: 4, height: 1, overflow: 'auto', position: 'relative' }}>
        <IconButton
          onClick={onClose}
          sx={{ position: 'absolute', top: 8, right: 8 }}
          aria-label="Close"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 40 40" fill="none">
            <path d="M27.0713 12.929L12.9292 27.0711" stroke="#666666" />
            <path d="M27.0713 27.071L12.9292 12.9289" stroke="#666666" />
          </svg>
        </IconButton>

        <Typography variant="h1" component="h2" sx={{ mb: 1, pr: 5 }}>
          Client account
        </Typography>
        <Typography sx={{ mb: 2, color: '#455445' }}>
          Edit login details, verify the account, or reset access when a client cannot sign in.
        </Typography>

        {responseError && <ErrorBox sx={{ mb: 2 }}>{responseError}</ErrorBox>}
        {successMessage && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {successMessage}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box component="form" onSubmit={handleSubmit(onSubmit)}>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
              {!verified && (
                <Alert severity="warning" sx={{ py: 0 }}>
                  Unverified
                </Alert>
              )}
              {disabled && (
                <Alert severity="error" sx={{ py: 0 }}>
                  Suspended
                </Alert>
              )}
              {verified && !disabled && (
                <Alert severity="success" sx={{ py: 0 }}>
                  Active
                </Alert>
              )}
            </Box>

            <InputText
              inputProps={{ ...register('email') }}
              type="email"
              label="Login email"
              placeholder="Email address"
              error={!!errors.email}
              helperText={errors.email?.message}
              fullWidth
            />
            <InputText
              inputProps={{ ...register('firstName') }}
              label="First name"
              placeholder="First name"
              error={!!errors.firstName}
              helperText={errors.firstName?.message}
              fullWidth
            />
            <InputText
              inputProps={{ ...register('lastName') }}
              label="Last name"
              placeholder="Last name"
              error={!!errors.lastName}
              helperText={errors.lastName?.message}
              fullWidth
            />
            <InputText
              inputProps={{ ...register('phoneNumber') }}
              type="tel"
              label="Phone number"
              placeholder="Phone number"
              error={!!errors.phoneNumber}
              helperText={errors.phoneNumber?.message}
              fullWidth
            />
            <Controller
              control={control}
              name="serviceAreaId"
              render={({ field: { onChange, onBlur, value } }) => (
                <SelectControl
                  label="Service area"
                  items={selectOptions}
                  onBlur={onBlur}
                  onChange={onChange}
                  value={value}
                />
              )}
            />
            {errors.serviceAreaId && (
              <ErrorBox sx={{ mb: 1 }}>{errors.serviceAreaId.message}</ErrorBox>
            )}

            <FormControlLabel
              control={
                <Switch
                  checked={verified}
                  onChange={(e) => setValue('verified', e.target.checked)}
                />
              }
              label="Email verified (allows login)"
              sx={{ mt: 1, display: 'flex' }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={disabled}
                  onChange={(e) => setValue('disabled', e.target.checked)}
                  color="error"
                />
              }
              label="Suspended (blocks login)"
              sx={{ display: 'flex', mb: 2 }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              disabled={saving}
              sx={{ mb: 3 }}
            >
              {saving ? <CircularProgress size={22} color="inherit" /> : 'Save account'}
            </Button>

            <Divider sx={{ mb: 2 }} />
            <Typography variant="h2" sx={{ mb: 1, fontSize: '20px' }}>
              Password help
            </Typography>
            <Typography sx={{ mb: 2, color: '#455445', fontSize: '14px' }}>
              Send a reset email, or set a temporary password and share it with the client.
              Call 970-749-0028 if they still cannot access the account.
            </Typography>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => setConfirmResetOpen(true)}
              disabled={passwordLoading}
              sx={{ mb: 2 }}
            >
              Send password reset email
            </Button>
            <InputText
              inputProps={{ ...register('temporaryPassword') }}
              type="password"
              label="Temporary password"
              placeholder="Temporary password"
              fullWidth
            />
            <Button
              fullWidth
              variant="contained"
              color="secondary"
              onClick={handleSetPassword}
              disabled={passwordLoading}
              sx={{ mb: 3 }}
            >
              {passwordLoading ? <CircularProgress size={22} color="inherit" /> : 'Set temporary password'}
            </Button>

            <Divider sx={{ mb: 2 }} />
            <Typography variant="h2" sx={{ mb: 1, fontSize: '20px' }}>
              Merge jobsites
            </Typography>
            <Typography sx={{ mb: 2, color: '#455445', fontSize: '14px' }}>
              Choose the jobsite to keep, then check duplicates to merge into it.
              Jobs move to the keep site and questionnaire notes are combined in When I Work.
              House Cleaning and Dog Walking at the same address should stay separate unless you intentionally merge them.
            </Typography>

            {sitesLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress size={24} />
              </Box>
            ) : sites.length === 0 ? (
              <Typography sx={{ mb: 2, color: '#6B746B', fontSize: '14px' }}>
                No jobsites found for this client.
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2 }}>
                {sites.map((site) => {
                  const isKeep = keepSiteId === site.id;
                  const isMerge = mergeSiteIds.includes(site.id);
                  const hasDuplicateHint = site.possibleDuplicateSiteIds.length > 0;

                  return (
                    <Box
                      key={site.id}
                      sx={{
                        border: '1px solid',
                        borderColor: isKeep ? '#316E59' : '#E3E7E3',
                        borderRadius: '8px',
                        p: 1.5,
                        backgroundColor: isKeep ? '#F7F9F7' : '#fff',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        <Radio
                          checked={isKeep}
                          onChange={() => {
                            setKeepSiteId(site.id);
                            setMergeSiteIds((prev) => prev.filter((id) => id !== site.id));
                          }}
                          size="small"
                          sx={{ mt: -0.5 }}
                        />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography sx={{ fontWeight: 600, fontSize: '14px', lineHeight: '20px' }}>
                            {site.name}
                          </Typography>
                          <Typography sx={{ fontSize: '13px', color: '#455445', mb: 0.5 }}>
                            {site.address}
                          </Typography>
                          <Typography sx={{ fontSize: '12px', color: '#6B746B' }}>
                            {site.jobCount} job{site.jobCount === 1 ? '' : 's'}
                            {site.categoryNames.length
                              ? ` · ${site.categoryNames.join(', ')}`
                              : ''}
                            {site.hasQuestionnaireNotes ? ' · has notes' : ' · no questionnaire notes'}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.75 }}>
                            {isKeep && <Chip size="small" label="Keep" color="success" />}
                            {hasDuplicateHint && (
                              <Chip size="small" label="Same address as another" variant="outlined" />
                            )}
                          </Box>
                        </Box>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={isMerge}
                              disabled={isKeep || !keepSiteId}
                              onChange={() => toggleMergeSite(site.id)}
                              size="small"
                            />
                          }
                          label="Merge"
                          sx={{ m: 0, ml: 1, '& .MuiFormControlLabel-label': { fontSize: '13px' } }}
                        />
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            )}

            <Button
              fullWidth
              variant="contained"
              disabled={!keepSiteId || mergeSiteIds.length === 0 || mergeLoading}
              onClick={() => setConfirmMergeOpen(true)}
            >
              {mergeLoading ? <CircularProgress size={22} color="inherit" /> : 'Merge selected jobsites'}
            </Button>
          </Box>
        )}
      </Box>
      <Dialog open={confirmResetOpen} onClose={() => setConfirmResetOpen(false)}>
        <DialogTitle>Send password reset?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            We will email a reset link and sign the client out of existing sessions. They will not be locked out.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmResetOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSendReset} disabled={passwordLoading}>
            {passwordLoading ? <CircularProgress size={18} color="inherit" /> : 'Send email'}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={confirmMergeOpen} onClose={() => !mergeLoading && setConfirmMergeOpen(false)}>
        <DialogTitle>Merge jobsites?</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            <Typography sx={{ mb: 1 }}>
              Keep <strong>{keepSite?.name}</strong> and merge {mergeSites.length} duplicate
              {mergeSites.length === 1 ? '' : 's'} into it.
            </Typography>
            <Typography sx={{ mb: 1, fontSize: '14px' }}>
              All jobs from the duplicates move to the keep site. Questionnaire / access notes are
              combined into the When I Work site description so assistants still see them.
            </Typography>
            {mergeSites.map((site) => (
              <Typography key={site.id} sx={{ fontSize: '13px', color: '#455445' }}>
                • {site.name} — {site.address}
              </Typography>
            ))}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmMergeOpen(false)} disabled={mergeLoading}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleMerge} disabled={mergeLoading}>
            {mergeLoading ? <CircularProgress size={18} color="inherit" /> : 'Merge'}
          </Button>
        </DialogActions>
      </Dialog>
    </Drawer>
  );
};
